/*
 * Shared live-PowerPoint-selection watcher for Deckorate's AI image features
 * (Sketch to Slide, Photo Headshot, ...). Reads the selection once on mount,
 * then re-reads on every DocumentSelectionChanged event, exposing the parsed
 * shape (or null) plus a non-fatal "not an image" notice for the panel's
 * empty state.
 */

import { useCallback, useEffect, useState } from "react";

export type SelectionErrorLogger = (reason: string, message: string) => void;

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function reportHandlerFailure(onError: SelectionErrorLogger, reason: string, fallback: string) {
  return (result: Office.AsyncResult<void>): void => {
    if (result.status === Office.AsyncResultStatus.Failed) {
      onError(reason, result.error.message || fallback);
    }
  };
}

export interface SelectionWatcherReasons {
  readonly handlerAdd: string;
  readonly handlerRemove: string;
}

/** Live selection: null while nothing usable is selected, else the shape read by `readShape`. */
export function useSelectionWatcher<T>(
  readShape: () => Promise<T>,
  notAnImageMessage: string,
  onError: SelectionErrorLogger,
  reasons: SelectionWatcherReasons,
): { shape: T | null; selectionError: string | null } {
  const [shape, setShape] = useState<T | null>(null);
  const [selectionError, setSelectionError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const next = await readShape();
      setShape(next);
      setSelectionError(null);
    } catch (error) {
      setShape(null);
      // The panel's default empty-state copy isn't an error worth surfacing —
      // only a real (non-image) selection is a notice.
      const message = getErrorMessage(error, "");
      setSelectionError(message === notAnImageMessage ? message : null);
    }
  }, [readShape, notAnImageMessage]);

  useEffect(() => {
    void refresh();

    try {
      Office.context.document.addHandlerAsync(
        Office.EventType.DocumentSelectionChanged,
        refresh,
        reportHandlerFailure(onError, reasons.handlerAdd, "addHandlerAsync failed"),
      );
    } catch (error) {
      onError(reasons.handlerAdd, getErrorMessage(error, "unsupported host"));
    }

    return () => {
      try {
        Office.context.document.removeHandlerAsync(
          Office.EventType.DocumentSelectionChanged,
          { handler: refresh } as Office.RemoveHandlerOptions,
          reportHandlerFailure(onError, reasons.handlerRemove, "removeHandlerAsync failed"),
        );
      } catch (error) {
        onError(reasons.handlerRemove, getErrorMessage(error, ""));
      }
    };
  }, [refresh, onError, reasons.handlerAdd, reasons.handlerRemove]);

  return { shape, selectionError };
}
